import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  Inject,
  AccumulationDataLabel,
  AccumulationTooltip,
  AccumulationLegend
} from '@syncfusion/ej2-react-charts';
import { FaUsers, FaBriefcase, FaBookOpen, FaBook } from 'react-icons/fa';
import { Header } from '../Components';
import axios from 'axios';
import { useBaseUrl } from '../Contexts/BaseUrlContext';

const Dash = () => {
  const [dashboard, setDashboard] = useState({
    total_loans: 0,
    inquiry_loans: 0,
    saction_loans: 0,
    disbursed_loans: 0,
    reject_loans: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [chartData, setChartData] = useState([]);
 

  const { baseUrl } = useBaseUrl();

  useEffect(() => {
    const fetchDashboard = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const authToken = localStorage.getItem('authToken');

        const formData = new FormData();
        formData.append('action', 'dashboard');
        formData.append('type', 'Reject File');

        const response = await axios.post(
  `${baseUrl}bank-system/loan.php`,
  formData,
  {
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    withCredentials: true,
  }
);

        const api = response.data;
        if (!api || !api.data) throw new Error('Unexpected API response');

        const d = api.data;

        const normalized = {
          total_loans: parseInt(d.total_loans, 10) || 0,
          inquiry_loans: parseInt(d.inquiry_loans, 10) || 0,
          saction_loans: parseInt(d.saction_loans, 10) || 0,
          disbursed_loans: parseInt(d.disbursed_loans, 10) || 0,
          reject_loans: parseInt(d.reject_loans, 10) || 0,
        };

        setDashboard(normalized);
      } catch (err) {
        setErrorMessage('Error fetching dashboard data: ' + (err.response?.data?.message || err.message));
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [baseUrl]);

  // Build chart data whenever dashboard changes
  useEffect(() => {
    // Create slices for inquiry, sanction, disbursed and remaining (optional Total)
    const inquiry = dashboard.inquiry_loans || 0;
    const sanction = dashboard.saction_loans || 0;
    const disbursed = dashboard.disbursed_loans || 0;
    const total = dashboard.total_loans || 0;

    // Option A: show the four requested items as slices.
    const data = [
      { x: 'Inquiry', y: inquiry, text: `${inquiry}` },
      { x: 'Sanction', y: sanction, text: `${sanction}` },
      { x: 'Disbursed', y: disbursed, text: `${disbursed}` },
      { x: 'Total Loans', y: total, text: `${total}` },
    ];

    // If you prefer "Total" to be the sum of others instead of API total, replace the last slice with:
    // { x: 'Remaining', y: Math.max(0, total - (inquiry + sanction + disbursed)), text: `${Math.max(0, total - (inquiry + sanction + disbursed))}` }

    setChartData(data);
  }, [dashboard]);

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-fadeIn min-h-screen">
      <Header category="Dashboard" title="Overview" />

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {errorMessage}
        </div>
      )}

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Link to="/syllabus" className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300">
          <div className="bg-red-500 text-white p-4 rounded-full">
            <FaBookOpen size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Total Loans</h3>
            <p className="text-2xl font-bold text-gray-700">{dashboard.total_loans}</p>
          </div>
        </Link>

        <Link to="/contact" className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300">
          <div className="bg-blue-500 text-white p-4 rounded-full">
            <FaUsers size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Total Inquiry</h3>
            <p className="text-2xl font-bold text-gray-700">{dashboard.inquiry_loans}</p>
          </div>
        </Link>

        <Link to="/inquiry" className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300">
          <div className="bg-blue-500 text-white p-4 rounded-full">
            <FaUsers size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Total Sanction</h3>
            <p className="text-2xl font-bold text-gray-700">{dashboard.saction_loans}</p>
          </div>
        </Link>

        <Link to="/placement" className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300">
          <div className="bg-green-500 text-white p-4 rounded-full">
            <FaBriefcase size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Total Disbursed</h3>
            <p className="text-2xl font-bold text-gray-700">{dashboard.disbursed_loans}</p>
          </div>
        </Link>

        <Link to="/course" className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300">
          <div className="bg-yellow-500 text-white p-4 rounded-full">
            <FaBook size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Total Reject-File</h3>
            <p className="text-2xl font-bold text-gray-700">{dashboard.reject_loans}</p>
          </div>
        </Link>
      </div>

      {/* Chart Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Loans Breakdown</h2>
        <AccumulationChartComponent
          id="loans-breakdown-chart"
          tooltip={{ enable: true, format: '<b>${point.x}</b><br/>${point.y}' }}
          legendSettings={{ visible: true, position: 'Bottom' }}
        >
          <Inject services={[AccumulationDataLabel, AccumulationTooltip, AccumulationLegend]} />
          <AccumulationSeriesCollectionDirective>
            <AccumulationSeriesDirective
              dataSource={chartData}
              xName="x"
              yName="y"
              dataLabel={{ visible: true, name: 'text', position: 'Outside', font: { fontWeight: '600' } }}
              radius="70%"
              explode={true}
              explodeOffset="10%"
              type="Pie"
            />
          </AccumulationSeriesCollectionDirective>
        </AccumulationChartComponent>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Links</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/Upload_Video" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 text-center">
            Inquiry List
          </Link>
          <Link to="/Video_List" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-300 text-center">
            Sanction-File
          </Link>
          <Link to="/Users" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition duration-300 text-center">
            Disbursed-File
          </Link>
          <Link to="/Video_List" className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 text-center">
            Reject-File
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dash;
